import { useState } from 'react'
import { Copy, Check } from 'lucide-react'
import { Button } from '../button/Button'
import { DescriptionList } from '../display/DescriptionList'

export interface AccountSettingsCardUser {
  id?: string
  email?: string
  role?: string
  tenantId?: string
  tenant_id?: string
  tenant_name?: string
  companyId?: string
  company_id?: string
  company_name?: string
  workspaceId?: string
  workspace_id?: string
  workspace_name?: string
}

/**
 * The heading this card carries. Exported because a caller that renders its own
 * section header above the card (and so passes `hideHeader`) needs the same
 * words — helpdesk retyped them, which is how one surface ended up saying
 * "Account Information" and the other "Account information".
 */
export const ACCOUNT_INFORMATION_TITLE = 'Account information'
export const ACCOUNT_INFORMATION_DESCRIPTION = 'Your user account details and identifiers.'

export interface AccountSettingsCardProps {
  user: AccountSettingsCardUser | null
  workspaceId?: string | null
  workspaceName?: string | null
  companyId?: string | null
  companyName?: string | null
  className?: string
  /** Suppress the in-card title and description (e.g. when the page renders its own section header above the card — use `ACCOUNT_INFORMATION_TITLE` / `_DESCRIPTION` there so the two agree). */
  hideHeader?: boolean
  /** Drop the App Role row (e.g. when the surrounding page already shows the role and owns the control that refreshes it). */
  hideRole?: boolean
  /** Drop the Company row — for a console with no company concept, where the row can only ever read "N/A". */
  hideCompany?: boolean
  /** Drop the Workspace row, for the same reason as `hideCompany`. */
  hideWorkspace?: boolean
  /**
   * What to call `user.role` in the row and in the copied text. Defaults to
   * "App Role", which is what Helpdesk and Playbook hand over. Dashboard
   * passes the org role from `tenant_users` instead, so it needs its own
   * label — the value and the label have to describe the same thing.
   */
  roleLabel?: string
}

/**
 * AccountSettingsCard - A shared component for displaying user account information
 * across all Airclou apps (Playbook, Helpdesk, etc.)
 */
export function AccountSettingsCard({
  user,
  workspaceId,
  workspaceName,
  companyId,
  companyName,
  className = '',
  hideHeader = false,
  hideRole = false,
  hideCompany = false,
  hideWorkspace = false,
  roleLabel = 'App Role',
}: AccountSettingsCardProps) {
  const [copied, setCopied] = useState(false)

  // Use props or fallback to user object properties
  const resolvedWorkspaceId = workspaceId || user?.workspaceId || user?.workspace_id
  const resolvedWorkspaceName = workspaceName || user?.workspace_name
  const resolvedCompanyId = companyId || user?.companyId || user?.company_id
  const resolvedCompanyName = companyName || user?.company_name

  const handleCopy = async () => {
    const lines: string[] = []
    const tenantId = user?.tenantId || user?.tenant_id || 'N/A'
    lines.push(
      user?.tenant_name ? `Tenant: ${user.tenant_name} (${tenantId})` : `Tenant ID: ${tenantId}`,
    )
    // A console that hides these has no such value anywhere, so the paste must
    // not carry them either — the "paste this into a ticket" flow this button
    // exists for would hand support `N/A` lines the operator never saw.
    if (!hideCompany) {
      if (resolvedCompanyName) {
        lines.push(`Company: ${resolvedCompanyName} (${resolvedCompanyId || 'N/A'})`)
      } else {
        lines.push(`Company ID: ${resolvedCompanyId || 'N/A'}`)
      }
    }
    if (!hideWorkspace) {
      if (resolvedWorkspaceName) {
        lines.push(`Workspace: ${resolvedWorkspaceName} (${resolvedWorkspaceId || 'N/A'})`)
      } else {
        lines.push(`Workspace ID: ${resolvedWorkspaceId || 'N/A'}`)
      }
    }
    lines.push(`User ID: ${user?.id || 'N/A'}`)
    // Not gated on `hideRole`: that flag means "the page shows the role
    // elsewhere", so the value is still on screen and belongs in the paste.
    // Whichever role that is, is the caller's choice — this copies back
    // exactly what it was handed in `user.role`.
    lines.push(`${roleLabel}: ${user?.role || 'N/A'}`)
    lines.push(`Email: ${user?.email || 'N/A'}`)

    try {
      await navigator.clipboard.writeText(lines.join('\n'))
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Fallback: no-op
    }
  }

  return (
    <div className={`max-w-2xl space-y-6 ${className}`}>
      <div className="relative">
        <div className="absolute right-4 top-5 z-10 sm:right-6">
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopy}
            className="gap-1.5"
          >
            {copied ? (
              <>
                <Check className="h-3.5 w-3.5" />
                Copied
              </>
            ) : (
              <>
                <Copy className="h-3.5 w-3.5" />
                Copy
              </>
            )}
          </Button>
        </div>
        <DescriptionList
          title={hideHeader ? undefined : ACCOUNT_INFORMATION_TITLE}
          description={hideHeader ? undefined : ACCOUNT_INFORMATION_DESCRIPTION}
          items={[
            {
              // `tenant_name` has been on this component's user type since it
              // was written and nothing ever drew it, so a caller that had the
              // organization's name still showed an opaque id. Same shape as
              // the two rows below when a name is there, and byte-identical to
              // the old row when it is not. Dashboard passes it; Playbook's
              // user carries it from the entitlements fetch (falling back to
              // the literal "Unknown" when that call fails, which now shows
              // here); Helpdesk builds its own object without it.
              term: user?.tenant_name ? 'Tenant' : 'Tenant ID',
              description: user?.tenant_name ? (
                <div className="flex flex-col gap-1">
                  <span className="font-medium">{user.tenant_name}</span>
                  <code className="text-xs font-mono bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded w-fit">
                    {user?.tenantId || user?.tenant_id || 'N/A'}
                  </code>
                </div>
              ) : (
                <code className="text-xs font-mono bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                  {user?.tenantId || user?.tenant_id || 'N/A'}
                </code>
              ),
            },
            ...(hideCompany ? [] : [{
              term: 'Company',
              description: resolvedCompanyName ? (
                <div className="flex flex-col gap-1">
                  <span className="font-medium">{resolvedCompanyName}</span>
                  <code className="text-xs font-mono bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded w-fit">
                    {resolvedCompanyId || 'N/A'}
                  </code>
                </div>
              ) : (
                <code className="text-xs font-mono bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                  {resolvedCompanyId || 'N/A'}
                </code>
              ),
            }]),
            ...(hideWorkspace ? [] : [{
              term: 'Workspace',
              description: resolvedWorkspaceName ? (
                <div className="flex flex-col gap-1">
                  <span className="font-medium">{resolvedWorkspaceName}</span>
                  <code className="text-xs font-mono bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded w-fit">
                    {resolvedWorkspaceId || 'N/A'}
                  </code>
                </div>
              ) : (
                <code className="text-xs font-mono bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                  {resolvedWorkspaceId || 'N/A'}
                </code>
              ),
            }]),
            {
              term: 'User ID',
              description: (
                <code className="text-xs font-mono bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                  {user?.id || 'N/A'}
                </code>
              ),
            },
            ...(hideRole ? [] : [{
              term: roleLabel,
              description: (
                <div className="flex flex-col gap-1">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary dark:bg-primary/20 dark:text-[hsl(var(--primary-hover))] capitalize w-fit">
                    {user?.role || 'N/A'}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Managed by your workspace admin. Changes may take a few minutes to fully apply.
                  </span>
                </div>
              ),
            }]),
            {
              term: 'Email',
              description: user?.email || 'N/A',
            },
          ]}
        />
      </div>
    </div>
  )
}
